import { Suspense, lazy, type ReactNode } from 'react'
import { ErrorDeRuta } from './FronteraDeError'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  type RouteObject,
} from 'react-router-dom'

import { AppLayout } from './AppLayout'
import type { VerticalSlug } from '../features/marketing/verticals'
import { PortalPage } from './PortalPage'
import { LoginPage } from '../features/auth/LoginPage'
import { RequireAuth } from '../features/auth/RequireAuth'
import { UsagePage } from '../features/billing/UsagePage'
import { ActivatePage } from '../features/onboarding/ActivatePage'
import { RequireActiveAccount } from '../features/onboarding/RequireActiveAccount'
import { ProjectPage } from '../features/projects/ProjectPage'
import { ProjectsPage } from '../features/projects/ProjectsPage'
import { SettingsPage } from '../features/settings/SettingsPage'
import { TeamPage } from '../features/team/TeamPage'
import { isMarketingHost } from '../lib/host'

/*
 * La web pública y el alta van en carga diferida.
 *
 * Quien abre el portal desde el móvil con datos —el caso que más nos importa—
 * no debería descargar la página de precios para ver las fotos de su boda. Con
 * `lazy` el marketing queda en su propio chunk y no lo paga nadie más.
 */
const LandingPage = lazy(() =>
  import('../features/marketing/LandingPage').then((m) => ({ default: m.LandingPage })),
)
const StudioLookupPage = lazy(() =>
  import('../features/marketing/StudioLookupPage').then((m) => ({ default: m.StudioLookupPage })),
)
const RegisterPage = lazy(() =>
  import('../features/onboarding/RegisterPage').then((m) => ({ default: m.RegisterPage })),
)

/*
 | Las páginas legales, cada una en su propio chunk.
 |
 | Son largas y casi nadie las abre, pero tienen que existir y estar enlazadas
 | desde el pie: el Libro de Reclamaciones es obligatorio en Perú y el
 | reglamento pide que sea accesible desde la página principal.
 */
const TermsPage = lazy(() =>
  import('../features/marketing/legal/TermsPage').then((m) => ({ default: m.TermsPage })),
)
const PrivacyPage = lazy(() =>
  import('../features/marketing/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const AcceptInvitationPage = lazy(() =>
  import('../features/onboarding/AcceptInvitationPage').then((m) => ({
    default: m.AcceptInvitationPage,
  })),
)
const ComplaintBookPage = lazy(() =>
  import('../features/marketing/legal/ComplaintBookPage').then((m) => ({
    default: m.ComplaintBookPage,
  })),
)

function Deferred({ children }: { children: ReactNode }) {
  // Sin texto de «cargando»: el chunk llega en milisegundos y un parpadeo de
  // texto se percibe peor que un instante en blanco.
  return <Suspense fallback={<div className="min-h-dvh" />}>{children}</Suspense>
}

/**
 * La raíz significa cosas distintas según el dominio desde el que se pida.
 *
 *   chasquitambo.com       → la web pública
 *   acme.chasquitambo.com  → el panel del estudio
 *
 * Es la misma SPA en ambos: separar los dos frontends duplicaría el cliente de
 * API, la sesión y los estilos para ahorrar unos KB que la carga diferida ya
 * ahorra.
 */
function Home() {
  if (isMarketingHost()) {
    return (
      <Deferred>
        <LandingPage />
      </Deferred>
    )
  }

  return <Navigate to="/projects" replace />
}

/**
 * Tres layouts distintos, y es una decisión de producto, no de estilo:
 *
 *   MarketingLayout — la web pública. Se lee de un vistazo y lleva a un sitio.
 *   AppLayout       — el estudio. Uso mayoritario desde escritorio, denso en
 *                     información, con navegación lateral.
 *   PortalLayout    — el cliente final. Uso mayoritario desde MÓVIL: llega por
 *                     un enlace de WhatsApp para ver las fotos de su boda. Sin
 *                     navegación, sin fricción, optimizado para el pulgar.
 *
 * Los tres responsive, pero optimizados para casos distintos. Construir uno solo
 * que sirva a los tres acaba siendo mediocre en los tres.
 */
/**
 * Una landing por vertical, con el mismo producto detrás.
 *
 * No cuelgan del MENÚ —un desplegable obligaría a elegir antes de entender—
 * sino de la sección «para quién» de la portada, que va después de explicar qué
 * es esto. Antes no colgaban de ningún sitio: existían y no las encontraba
 * nadie, que es como no existir.
 *
 * `/fotografia` es la portada anterior, la que hablaba sólo de bodas. Se
 * conserva entera: es la única de las tres cuyo texto está probado contra el
 * mercado, y el segmento sigue siendo el principal.
 */
function VerticalLanding({ vertical }: { vertical: VerticalSlug }) {
  if (! isMarketingHost()) {
    return <Navigate to="/projects" replace />
  }

  return (
    <Deferred>
      <LandingPage vertical={vertical} />
    </Deferred>
  )
}

const rutas: RouteObject[] = [
  { path: '/', element: <Home /> },
  { path: '/fotografia', element: <VerticalLanding vertical="fotografia" /> },
  { path: '/video', element: <VerticalLanding vertical="video" /> },
  { path: '/arquitectura', element: <VerticalLanding vertical="arquitectura" /> },

  /*
   | Sin comprobar `isMarketingHost()`: se sirven igual desde el dominio de un
   | estudio. Quien va a reclamar suele estar dentro de su panel, y mandarle a
   | otro dominio a mitad de camino es la peor forma de atender un reclamo.
   */
  /*
   | Aceptar una invitación al equipo.
   |
   | Fuera del guardián de sesión y fuera del layout del panel: quien llega aquí
   | todavía no tiene contraseña, así que no puede haber sesión que comprobar.
   */
  {
    path: '/invitacion/:token',
    element: (
      <Deferred>
        <AcceptInvitationPage />
      </Deferred>
    ),
  },

  {
    path: '/terminos',
    element: (
      <Deferred>
        <TermsPage />
      </Deferred>
    ),
  },
  {
    path: '/privacidad',
    element: (
      <Deferred>
        <PrivacyPage />
      </Deferred>
    ),
  },
  {
    path: '/libro-de-reclamaciones',
    element: (
      <Deferred>
        <ComplaintBookPage />
      </Deferred>
    ),
  },

  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    // Alta de estudios. Fuera de RequireAuth —todavía no hay cuenta— y servida
    // desde el host de la aplicación, porque cuando se rellena este formulario
    // el tenant aún no existe y no hay Host que resolver.
    path: '/registro',
    element: (
      <Deferred>
        <RegisterPage />
      </Deferred>
    ),
  },
  {
    // Cada estudio entra por su propia dirección; aquí se pregunta cuál.
    path: '/entrar',
    element: (
      <Deferred>
        <StudioLookupPage />
      </Deferred>
    ),
  },

  {
    // Ruta sin `path`: agrupa por layout sin ocupar «/», que ahora es de la web.
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      // Fuera del guardián: es justo la pantalla a la que manda, y «Plan y uso»
      // debe seguir accesible para poder cambiar de plan antes de pagar.
      { path: 'activar', element: <ActivatePage /> },
      { path: 'usage', element: <UsagePage /> },
      // Fuera del guardián, como «Plan y uso»: el color y el tema son de la
      // cuenta, no del servicio, y no hay razón para bloquearlos por un impago.
      { path: 'settings', element: <SettingsPage /> },
      // Fuera del guardián, como el resto de la cuenta: quitarle a alguien el
      // acceso no puede depender de estar al día con el pago.
      { path: 'team', element: <TeamPage /> },
      {
        element: <RequireActiveAccount />,
        children: [
          { path: 'projects', element: <ProjectsPage /> },
          { path: 'projects/:projectId', element: <ProjectPage /> },
        ],
      },
    ],
  },

  {
    // Portal público del cliente final. Fuera de RequireAuth: la credencial
    // es el enlace, no una sesión de estudio.
    //
    // Ruta corta a propósito: se comparte por WhatsApp y a menudo se dicta por
    // teléfono. `/g/` gana a `/galeria/compartida/`.
    path: '/g/:token',
    element: <PortalPage />,
  },
]

/**
 * Dónde queda la página al cambiar de ruta.
 *
 * `createBrowserRouter` NO toca el scroll por su cuenta: al navegar deja el
 * documento donde estaba, así que quien pulsaba una tarjeta de «para quién»
 * —que está a media página— aterrizaba a media página de OTRA, con un texto
 * distinto y sin contexto. Parecía un fallo de carga.
 *
 * `ScrollRestoration` hace las tres cosas que hay que hacer, y por eso se usa
 * en vez de un `scrollTo(0, 0)` a mano:
 *
 *   - Navegación nueva → arriba del todo.
 *   - Atrás y adelante → devuelve la posición que tenías, que es justo lo que
 *     un `scrollTo(0, 0)` rompería: volver de una galería de 400 fotos al
 *     principio de la lista es peor que no hacer nada.
 *   - Con `#hash` → hasta el elemento.
 *
 * Va en una ruta raíz sin `path` para que valga en las tres caras de la app:
 * la web, el panel y el portal. El fallo no era de la portada, era del router.
 */
function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  )
}

/*
 * `errorElement` en la raíz y no en cada ruta: cuelga de la misma que ya envuelve
 * a todas, así que cubre las cuarenta sin que nadie tenga que acordarse al
 * añadir la cuarenta y una. Y va A LA ALTURA de `Root`, no dentro: si lo que
 * revienta es el propio `Root`, una frontera dentro de él no llega a montarse.
 */
export const router = createBrowserRouter([
  { element: <Root />, errorElement: <ErrorDeRuta />, children: rutas },
])
